/**
 * The hairpins of a part as it is played: where each one's slope begins and where it ends.
 *
 * A hairpin is drawn from one event to another, and read in the written order it slopes from
 * the first to the second. The played order is not the written one. A repeat goes back from
 * the middle of a hairpin, and a second-time bar is played in place of the bar a hairpin ran
 * on into, so the event it ends at is not reached on that time through. Kept open until that
 * event did come round, it would slope over everything played in between -- or, begun again
 * first, would not be heard at all on the time it was left.
 *
 * So a hairpin is over as well when the played order comes to a bar it does not cover, or
 * back to the bar it began in: it slopes to that bar line, which is as far as the player was
 * able to take it. The end of the piece is a bar no hairpin covers. A hairpin whose last event
 * is not in the part covers the bar it begins in.
 */

import type { EventId, SpannerId } from "../document/ids";
import type { Part, Score } from "../document/types";
import type { Ramp } from "./dynamics";
import { spansOf } from "./spans";

/** The hairpins open in a part as it is played. */
export interface OpenHairpins {
  /**
   * Come to a bar, an index of {@link Score.measures}, beginning at `ticks` of the played
   * order: the slopes of the hairpins that are over at its bar line.
   */
  bar(index: number, ticks: number): readonly Ramp[];
  /**
   * Read an event of the bar come to, standing at `at` and `ticks` long: the hairpins that
   * begin at it are open, and the slopes of the ones that end at it are returned.
   */
  event(id: EventId, at: number, ticks: number): readonly Ramp[];
}

/** An open hairpin: where it began, and the bars it covers after the one it began in. */
interface Open {
  readonly from: number;
  readonly begun: number;
  readonly last: number;
  readonly rising: boolean;
}

export function hairpinsOf(score: Score, part: Part): OpenHairpins {
  const { opens, closes, lastBar } = spansOf(score, part, ["crescendo", "diminuendo"]);
  const open = new Map<SpannerId, Open>();
  let reading = 0;
  return {
    bar(index, ticks) {
      reading = index;
      const over: Ramp[] = [];
      for (const [id, hairpin] of open) {
        if (index > hairpin.begun && index <= hairpin.last) continue;
        open.delete(id);
        over.push({ from: hairpin.from, to: ticks, rising: hairpin.rising });
      }
      return over;
    },
    event(id, at, ticks) {
      for (const span of opens.get(id) ?? []) {
        open.set(span.id, {
          from: at,
          begun: reading,
          last: lastBar.get(span.id) ?? reading,
          rising: span.kind === "crescendo",
        });
      }
      const over: Ramp[] = [];
      for (const span of closes.get(id) ?? []) {
        const hairpin = open.get(span.id);
        if (hairpin === undefined) continue;
        open.delete(span.id);
        // A hairpin drawn under one note alone -- the last of a bar, say, running to the bar
        // line -- swells or fades through that note rather than going unheard.
        const to = at === hairpin.from ? at + ticks : at;
        over.push({ from: hairpin.from, to, rising: hairpin.rising });
      }
      return over;
    },
  };
}
